import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';



function AdminBlog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [showForm, setShowForm] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    meta_title: '',
    meta_description: '',
    keywords: '',
    featured_image: '',
    author: 'Flirteen Team',
    category: '',
    published: false
  });

  const [categories] = useState([
    'Dating Tips',
    'Relationship Advice', 
    'Online Dating',
    'Love & Romance',
    'Dating Safety',
    'Success Stories'
  ]);

  useEffect(() => {
    getAllPosts();
  }, []);

  const getAllPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (data) {
        setPosts(data);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim('-');
  };

  const handleTitleChange = (title) => {
    setFormData({
      ...formData,
      title: title,
      slug: generateSlug(title),
      meta_title: title // Auto-fill meta title
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      meta_title: '',
      meta_description: '',
      keywords: '',
      featured_image: '',
      author: 'Flirteen Team',
      category: '',
      published: false
    });
    setIsEditing(false);
    setEditingPost(null);
    setShowForm(false);
  };

  const handleEdit = (post) => {
    setFormData({
      title: post.title || '',
      slug: post.slug || '',
      content: post.content || '',
      excerpt: post.excerpt || '',
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      keywords: post.keywords || '',
      featured_image: post.featured_image || '',
      author: post.author || 'Flirteen Team',
      category: post.category || '',
      published: post.published || false
    });
    setEditingPost(post);
    setIsEditing(true);
    setShowForm(true);
  };

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.title || !formData.content) {
        alert('Title and content are required!');
        return;
      }

      if (isEditing && editingPost) {
        // Update existing post
        const { error } = await supabase
          .from('blog_posts')
          .update({
            ...formData,
            updated_at: new Date().toISOString()
          })
          .eq('id', editingPost.id);

        if (error) throw error;
        alert('Post updated successfully!');
        
        // Update sitemap if post is published
        if (formData.published) {
          await updateSitemap();
        }
      } else {
        // Create new post
        const { error } = await supabase
          .from('blog_posts')
          .insert({
            ...formData,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        if (error) throw error;
        alert('Post created successfully!');
        
        // Update sitemap if post is published
        if (formData.published) {
          await updateSitemap();
        }
      }

      resetForm();
      getAllPosts();
    } catch (error) {
      console.error('Error saving post:', error);
      alert('Error saving post: ' + error.message);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', postId);

      if (error) throw error;
      
      alert('Post deleted successfully!');
      getAllPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
      alert('Error deleting post');
    }
  };

  const togglePublished = async (post) => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .update({ 
          published: !post.published,
          updated_at: new Date().toISOString()
        })
        .eq('id', post.id);

      if (error) throw error;
      getAllPosts();
      
      // Update sitemap when publishing/unpublishing
      await updateSitemap();
    } catch (error) {
      console.error('Error updating post:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const updateSitemap = async () => {
    try {
      // Get all published blog posts
      const { data: publishedPosts, error } = await supabase
        .from('blog_posts')
        .select('slug, updated_at')
        .eq('published', true)
        .order('updated_at', { ascending: false });

      if (error) throw error;

      // Generate complete sitemap XML
      const currentDate = new Date().toISOString().split('T')[0];
      
      // Static pages
      const staticPages = [
        { url: '', priority: '1.0' },
        { url: 'about', priority: '0.8' },
        { url: 'contact', priority: '0.8' },
        { url: 'privacy', priority: '0.6' },
        { url: 'terms', priority: '0.6' },
        { url: 'blog', priority: '0.9' }
      ];

      const staticEntries = staticPages.map(page => 
        `  <url>
    <loc>https://flirteen.com/${page.url}</loc>
    <lastmod>${currentDate}</lastmod>
    <changefreq>monthly</changefreq>
    <priority>${page.priority}</priority>
  </url>`
      ).join('\n');

      // Blog post entries
      const blogEntries = publishedPosts.map(post => 
        `  <url>
    <loc>https://flirteen.com/blog/${post.slug}</loc>
    <lastmod>${new Date(post.updated_at).toISOString().split('T')[0]}</lastmod>
    <changefreq>weekly</changefreq>
    <priority>0.8</priority>
  </url>`
      ).join('\n');

      // Complete sitemap XML
      const sitemapXML = `<?xml version="1.0" encoding="UTF-8"?>
<urlset xmlns="http://www.sitemaps.org/schemas/sitemap/0.9">
${staticEntries}
${blogEntries}
</urlset>`;

      // Display the sitemap for copying
      console.log('Complete Sitemap XML:', sitemapXML);
      
      // Create a downloadable file
      const blob = new Blob([sitemapXML], { type: 'application/xml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'sitemap.xml';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      alert(`Sitemap generated with ${publishedPosts.length} blog posts! File downloaded as sitemap.xml - replace your existing sitemap.xml with this file.`);
      
    } catch (error) {
      console.error('Error generating sitemap:', error);
      alert('Error generating sitemap: ' + error.message);
    }
  };
  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '60vh',
        color: 'white',
        fontSize: '18px'
      }}>
        Loading blog management...
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
      padding: '20px'
    }}>
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto'
      }}>
        {/* Header */}
        <div style={{ 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '30px'
        }}>
          <h1 style={{ 
            fontSize: '32px', 
            fontWeight: '700',
            background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            margin: 0
          }}>
            📝 Blog Management
          </h1>
          
          <div style={{ display: 'flex', gap: '10px' }}>
            <button
              onClick={updateSitemap}
              style={{
                background: 'linear-gradient(90deg, #ffc107, #fd7e14)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '12px 20px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif'
              }}
            >
              🗺️ Update Sitemap
            </button>
            
            <button
              onClick={() => setShowForm(!showForm)}
              style={{
                background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                color: 'white',
                border: 'none',
                borderRadius: '10px',
                padding: '12px 24px',
                fontSize: '16px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: 'Poppins, sans-serif'
              }}
            >
              {showForm ? 'Cancel' : '+ New Post'}
            </button>
          </div>
  </div>

        {/* Form */}
        {showForm && (
          <div style={{
            background: 'rgba(255, 255, 255, 0.08)',
            borderRadius: '20px',
            padding: '30px',
            marginBottom: '30px',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }}>
            <h3 style={{ 
              color: '#00E9FF', 
              marginBottom: '25px',
              fontSize: '20px'
            }}>
              {isEditing ? '✏️ Edit Post' : '📝 Create New Post'}
            </h3>

            <div style={{ display: 'grid', gap: '20px' }}>
              {/* Title */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#00E9FF'
                }}>
                  Post Title *
                </label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleTitleChange(e.target.value)}
                  placeholder="e.g., 10 Best Dating Tips for 2025"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Slug */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#00E9FF'
                }}>
                  URL Slug (SEO)
                </label>
                <input
                  type="text"
                  value={formData.slug}
                  onChange={(e) => setFormData({...formData, slug: e.target.value})}
                  placeholder="10-best-dating-tips-2025"
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontSize: '16px',
                    boxSizing: 'border-box'
                  }}
                />
                <small style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '12px' }}>
                  URL: yoursite.com/blog/{formData.slug}
                </small>
              </div>

              {/* Category & Author Row */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '600', 
                    color: '#00E9FF'
                  }}>
                    Category
                  </label>
                  <select
                    value={formData.category}
                    onChange={(e) => setFormData({...formData, category: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'white',
                      fontSize: '16px'
                    }}
                  >
                    <option value="" style={{ background: '#1a1a2e' }}>Select Category</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat} style={{ background: '#1a1a2e' }}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label style={{ 
                    display: 'block', 
                    marginBottom: '8px', 
                    fontWeight: '600', 
                    color: '#00E9FF'
                  }}>
                    Author
                  </label>
                  <input
                    type="text"
                    value={formData.author}
                    onChange={(e) => setFormData({...formData, author: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: 'white',
                      fontSize: '16px',
                      boxSizing: 'border-box'
                    }}
                  />
                </div>
              </div>

              {/* Excerpt */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#00E9FF'
                }}>
                  Excerpt (Summary)
                </label>
                <textarea
                  value={formData.excerpt}
                  onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                  placeholder="Brief summary of the post (150-160 characters)"
                  rows={3}
                  style={{
                    width: '100%',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontSize: '16px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              {/* Content */}
              <div>
                <label style={{ 
                  display: 'block', 
                  marginBottom: '8px', 
                  fontWeight: '600', 
                  color: '#00E9FF'
                }}>
                  Content (HTML) *
                </label>
                <div style={{ marginBottom: '10px' }}>
                  <small style={{ 
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '12px',
                    display: 'block',
                    marginBottom: '5px'
                  }}>
                    📝 Write your content using HTML tags. Common tags: &lt;h2&gt;&lt;/h2&gt;, &lt;p&gt;&lt;/p&gt;, &lt;ul&gt;&lt;li&gt;&lt;/li&gt;&lt;/ul&gt;, &lt;strong&gt;&lt;/strong&gt;, &lt;em&gt;&lt;/em&gt;
                  </small>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.getElementById('content-textarea');
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const selectedText = textarea.value.substring(start, end) || 'heading text';
                        const newValue = textarea.value.substring(0, start) + `<h2>${selectedText}</h2>` + textarea.value.substring(end);
                        setFormData({...formData, content: newValue});
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + 4, start + 4 + selectedText.length);
                        }, 0);
                      }}
                      style={{
                        padding: '4px 8px',
                        background: 'rgba(0, 233, 255, 0.2)',
                        border: '1px solid rgba(0, 233, 255, 0.4)',
                        borderRadius: '4px',
                        color: '#00E9FF',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      H2
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.getElementById('content-textarea');
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const selectedText = textarea.value.substring(start, end) || 'paragraph text';
                        const newValue = textarea.value.substring(0, start) + `<p>${selectedText}</p>` + textarea.value.substring(end);
                        setFormData({...formData, content: newValue});
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + 3, start + 3 + selectedText.length);
                        }, 0);
                      }}
                      style={{
                        padding: '4px 8px',
                        background: 'rgba(0, 233, 255, 0.2)',
                        border: '1px solid rgba(0, 233, 255, 0.4)',
                        borderRadius: '4px',
                        color: '#00E9FF',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      P
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.getElementById('content-textarea');
                        const start = textarea.selectionStart;
                        const template = `<ul>\n  <li>First item</li>\n  <li>Second item</li>\n  <li>Third item</li>\n</ul>`;
                        const newValue = textarea.value.substring(0, start) + template + textarea.value.substring(start);
                        setFormData({...formData, content: newValue});
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + 9, start + 19);
                        }, 0);
                      }}
                      style={{
                        padding: '4px 8px',
                        background: 'rgba(0, 233, 255, 0.2)',
                        border: '1px solid rgba(0, 233, 255, 0.4)',
                        borderRadius: '4px',
                        color: '#00E9FF',
                        fontSize: '11px',
                        cursor: 'pointer'
                      }}
                    >
                      • List
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        const textarea = document.getElementById('content-textarea');
                        const start = textarea.selectionStart;
                        const end = textarea.selectionEnd;
                        const selectedText = textarea.value.substring(start, end) || 'bold text';
                        const newValue = textarea.value.substring(0, start) + `<strong>${selectedText}</strong>` + textarea.value.substring(end);
                        setFormData({...formData, content: newValue});
                        setTimeout(() => {
                          textarea.focus();
                          textarea.setSelectionRange(start + 8, start + 8 + selectedText.length);
                        }, 0);
                      }}
                      style={{
                        padding: '4px 8px',
                        background: 'rgba(0, 233, 255, 0.2)',
                        border: '1px solid rgba(0, 233, 255, 0.4)',
                        borderRadius: '4px',
                        color: '#00E9FF',
                        fontSize: '11px',
                        cursor: 'pointer',
                        fontWeight: 'bold'
                      }}
                    >
                      Bold
                    </button>
                  </div>
                </div>
                <textarea
                  id="content-textarea"
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  placeholder={`Write your blog content using HTML tags. Example:

<h2>Your Heading Here</h2>
<p>Your paragraph content goes here. Use <strong>bold</strong> and <em>italic</em> text as needed.</p>

<h3>Subheading</h3>
<ul>
  <li>First bullet point</li>
  <li>Second bullet point</li>
  <li>Third bullet point</li>
</ul>

<p>Another paragraph with <a href="https://example.com">a link</a> and more content.</p>`}
                  rows={15}
                  style={{
                    width: '100%',
                    padding: '15px',
                    borderRadius: '8px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    background: 'rgba(255, 255, 255, 0.05)',
                    color: 'white',
                    fontSize: '14px',
                    fontFamily: 'Monaco, Consolas, "Courier New", monospace',
                    resize: 'vertical',
                    boxSizing: 'border-box',
                    lineHeight: '1.4'
                  }}
                />
                
                {/* Live Preview */}
                <div style={{ marginTop: '15px' }}>
                  <button
                    type="button"
                    onClick={() => {
                      const preview = document.getElementById('live-preview');
                      preview.style.display = preview.style.display === 'none' ? 'block' : 'none';
                    }}
                    style={{
                      padding: '8px 16px',
                      background: 'rgba(0, 233, 255, 0.2)',
                      border: '1px solid rgba(0, 233, 255, 0.4)',
                      borderRadius: '6px',
                      color: '#00E9FF',
                      cursor: 'pointer',
                      fontSize: '14px',
                      marginBottom: '10px'
                    }}
                  >
                    👁️ Toggle Live Preview
                  </button>
                  
                  <div
                    id="live-preview"
                    style={{
                      display: 'none',
                      padding: '20px',
                      background: 'rgba(255, 255, 255, 0.03)',
                      borderRadius: '8px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      marginTop: '10px'
                    }}
                  >
                    <h4 style={{ color: '#00E9FF', marginBottom: '15px', fontSize: '16px' }}>
                      📖 Live Preview:
                    </h4>
                    <div 
                      dangerouslySetInnerHTML={{ __html: formData.content }}
                      style={{
                        color: 'rgba(255, 255, 255, 0.9)',
                        lineHeight: '1.7'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* SEO Section */}
              <div style={{
                background: 'rgba(0, 233, 255, 0.1)',
                borderRadius: '10px',
                padding: '20px',
                border: '1px solid rgba(0, 233, 255, 0.3)'
              }}>
                <h4 style={{ color: '#00E9FF', marginBottom: '15px' }}>🔍 SEO Settings</h4>
                
                <div style={{ display: 'grid', gap: '15px' }}>
                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600', 
                      color: '#00E9FF'
                    }}>
                      Meta Title
                    </label>
                    <input
                      type="text"
                      value={formData.meta_title}
                      onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                      placeholder="SEO title (60 characters max)"
                      maxLength={60}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                    <small style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      {formData.meta_title.length}/60 characters
                    </small>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600', 
                      color: '#00E9FF'
                    }}>
                      Meta Description
                    </label>
                    <textarea
                      value={formData.meta_description}
                      onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                      placeholder="SEO description (160 characters max)"
                      maxLength={160}
                      rows={3}
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '16px',
                        resize: 'vertical',
                        boxSizing: 'border-box'
                      }}
                    />
                    <small style={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                      {formData.meta_description.length}/160 characters
                    </small>
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600', 
                      color: '#00E9FF'
                    }}>
                      Keywords (SEO)
                    </label>
                    <input
                      type="text"
                      value={formData.keywords}
                      onChange={(e) => setFormData({...formData, keywords: e.target.value})}
                      placeholder="dating tips, relationships, love, romance"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ 
                      display: 'block', 
                      marginBottom: '8px', 
                      fontWeight: '600', 
                      color: '#00E9FF'
                    }}>
                      Featured Image URL
                    </label>
                    <input
                      type="url"
                      value={formData.featured_image}
                      onChange={(e) => setFormData({...formData, featured_image: e.target.value})}
                      placeholder="https://example.com/image.jpg"
                      style={{
                        width: '100%',
                        padding: '12px 16px',
                        borderRadius: '8px',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: 'white',
                        fontSize: '16px',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              {/* Publish Toggle */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <input
                  type="checkbox"
                  id="published"
                  checked={formData.published}
                  onChange={(e) => setFormData({...formData, published: e.target.checked})}
                  style={{ transform: 'scale(1.2)' }}
                />
                <label htmlFor="published" style={{ color: 'white', fontWeight: '600' }}>
                  Publish immediately
                </label>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '15px', justifyContent: 'flex-end' }}>
                <button
                  onClick={resetForm}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  style={{
                    background: 'linear-gradient(90deg, #00E9FF, #8A2BE2)',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  {isEditing ? 'Update Post' : 'Create Post'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Posts List */}
        <div style={{
          background: 'rgba(255, 255, 255, 0.08)',
          borderRadius: '20px',
          padding: '30px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <h3 style={{ color: '#00E9FF', marginBottom: '25px' }}>
            📚 All Posts ({posts.length})
          </h3>

          {posts.length === 0 ? (
            <div style={{
              textAlign: 'center',
              color: 'rgba(255, 255, 255, 0.7)',
              fontSize: '16px',
              padding: '40px'
            }}>
              No posts created yet. Click "New Post" to get started!
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '20px' }}>
              {posts.map(post => (
                <div key={post.id} style={{
                  background: 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '15px',
                  padding: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  display: 'grid',
                  gridTemplateColumns: '1fr auto',
                  gap: '20px',
                  alignItems: 'center'
                }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                      <h4 style={{
                        color: 'white',
                        margin: 0,
                        fontSize: '18px',
                        fontWeight: '600'
                      }}>
                        {post.title}
                      </h4>
                      <span style={{
                        background: post.published ? '#51cf66' : '#ffd43b',
                        color: post.published ? 'white' : '#000',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '600'
                      }}>
                        {post.published ? 'PUBLISHED' : 'DRAFT'}
                      </span>
                    </div>
                    
                    <div style={{
                      color: 'rgba(255, 255, 255, 0.6)',
                      fontSize: '14px',
                      marginBottom: '8px'
                    }}>
                      <span>📅 {formatDate(post.created_at)}</span>
                      <span style={{ margin: '0 10px' }}>•</span>
                      <span>✍️ {post.author}</span>
                      {post.category && (
                        <>
                          <span style={{ margin: '0 10px' }}>•</span>
                          <span>🏷️ {post.category}</span>
                        </>
                      )}
                    </div>
                    
                    {post.excerpt && (
                      <p style={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '14px',
                        margin: 0,
                        lineHeight: '1.4'
                      }}>
                        {post.excerpt}
                      </p>
                    )}
                  </div>

                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => togglePublished(post)}
                      style={{
                        background: post.published ? '#ffd43b' : '#51cf66',
                        color: post.published ? '#000' : 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      {post.published ? 'Unpublish' : 'Publish'}
                    </button>
                    
                    <button
                      onClick={() => handleEdit(post)}
                      style={{
                        background: '#00E9FF',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Edit
                    </button>
                    
                    <button
                      onClick={() => handleDelete(post.id)}
                      style={{
                        background: '#ff6b6b',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '8px 12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminBlog;